import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  LegacyRef,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "./DeleteAdvertisementModal.module.scss";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import { useAdvertisementsStore } from "../../../store/advertisements";
import { useAlertStore } from "../../../store/alertStore";

interface Props {
  advertisementId: string | null;
  onSuccess?: () => void;
}

export interface DeleteAdvertisementModalRef {
  openModal: () => void;
  closeModal: () => void;
}

const DeleteAdvertisementModal = forwardRef<DeleteAdvertisementModalRef, Props>(
  ({ advertisementId, onSuccess }, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const deleteAdvertisement = useAdvertisementsStore(
      (s) => s.deleteAdvertisement
    );
    const fetchAdvertisements = useAdvertisementsStore(
      (s) => s.fetchAdvertisements
    );
    const { showError, showSuccess } = useAlertStore();

    const openModal = useCallback(() => {
      setOpen(true);
    }, []);

    const closeModal = useCallback(() => {
      setOpen(false);
      setDeleting(false);
    }, []);

    const stopPropagation = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
    }, []);

    const handleConfirm = useCallback(async () => {
      if (!advertisementId) return;

      try {
        setDeleting(true);
        await deleteAdvertisement(advertisementId);
        showSuccess(t("advertisement_delete_success"));
        await fetchAdvertisements(); // Refresh the list
        closeModal();
        onSuccess?.(); // Call optional success callback
      } catch (error) {
        console.error("Error deleting advertisement:", error);
        showError(t("advertisement_delete_error"));
      } finally {
        setDeleting(false);
      }
    }, [
      advertisementId,
      deleteAdvertisement,
      showSuccess,
      showError,
      t,
      fetchAdvertisements,
      closeModal,
      onSuccess,
    ]);

    useImperativeHandle(ref, () => ({
      openModal,
      closeModal,
    }));

    if (!open) return null;

    return (
      <div ref={ref as LegacyRef<HTMLDivElement>} className={styles.modal}>
        <div onClick={closeModal} className={styles.mask} />
        <div onClick={stopPropagation} className={styles.content}>
          {/* Close Button */}
          <div onClick={closeModal} className={styles.close_button}>
            <img src={"/assets/images/svg/cross_icon.svg"} alt="close" />
          </div>

          {/* Content */}
          <div className={styles.modal_body}>
            <h2 className={styles.title}>
              {t("advertisement_delete_confirm_title")}
            </h2>
            <p className={styles.description}>
              {t("advertisement_delete_confirm_description")}
            </p>
          </div>

          {/* Actions */}
          <div className={styles.actions_container}>
            <PrimaryButton
              onClick={handleConfirm}
              buttonType={
                deleting ? PrimaryButtonType.DISABLED : PrimaryButtonType.DANGER
              }
              disabled={deleting}>
              <p className={styles.button_text}>
                {deleting ? t("lbl_loading") : t("delete_confirm")}
              </p>
            </PrimaryButton>
            <PrimaryButton
              onClick={closeModal}
              buttonType={
                deleting
                  ? PrimaryButtonType.DISABLED
                  : PrimaryButtonType.PRIMARY_LIGHT
              }
              className={styles.cancel_button}
              disabled={deleting}>
              {t("closed")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }
);

DeleteAdvertisementModal.displayName = "DeleteAdvertisementModal";

export default DeleteAdvertisementModal;
